import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Link2, CheckCircle, XCircle, AlertTriangle, Plus, Trash2, ShieldAlert, Info } from 'lucide-react';

const EXAMPLE_SCHAIN = {
  ver: "1.0",
  complete: 1,
  nodes: [
    { asi: "directseller.com", sid: "00001", hp: 1 },
    { asi: "exchange.com", sid: "12345", hp: 1, rid: "BidRequest1", name: "Publisher Name", domain: "publisher.com" }
  ]
};

// ─── Ads.txt summary component ───────────────────────────────────────────────
function AdsTxtSummary({ atc }) {
  if (!atc) return null;
  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
          Publisher ads.txt / app-ads.txt Check
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', fontSize: '11px' }}>
          <span className="badge badge-cyan">{atc.publisherDomain}</span>
          <span className="badge badge-muted" style={{ fontSize: '10px' }}>{atc.domainSource}</span>
          <span className={`badge ${atc.adsTxtFetched ? 'badge-green' : 'badge-muted'}`}>ads.txt: {atc.adsTxtFetched ? '✓' : '✗'}</span>
          <span className={`badge ${atc.appAdsTxtFetched ? 'badge-green' : 'badge-muted'}`}>app-ads.txt: {atc.appAdsTxtFetched ? '✓' : '✗'}</span>
        </div>
      </div>
      {atc.nodeResults?.map((nr, i) => (
        <div key={i} style={{ marginBottom: '10px', padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text-primary)', marginBottom: '8px' }}>
            <span style={{ color: 'var(--cyan)' }}>{nr.asi}</span>
            <span className="badge badge-muted" style={{ marginLeft: '8px' }}>sid: {nr.sid}</span>
          </div>
          {['ads.txt', 'app-ads.txt'].map(fileType => {
            const f = nr.files?.[fileType];
            if (!f) return null;
            if (!f.fetched) return (
              <div key={fileType} style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                {fileType}: <span style={{ color: 'var(--text-muted)' }}>not found (HTTP {f.httpStatus || 'error'})</span>
              </div>
            );
            return (
              <div key={fileType} style={{ marginBottom: '6px' }}>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '3px' }}>
                  {f.found
                    ? <span className="badge badge-green">✓ {fileType}: found</span>
                    : <span className="badge badge-red">✗ {fileType}: not listed</span>}
                  {f.isDirect && <span className="badge badge-cyan">DIRECT</span>}
                  {f.isReseller && <span className="badge badge-purple">RESELLER</span>}
                  {f.count > 1 && <span className="badge badge-amber">{f.count}× duplicate</span>}
                </div>
                {f.lines?.map((line, li) => (
                  <div key={li} style={{ background: 'var(--bg-base)', padding: '3px 8px', borderRadius: '3px', border: '1px solid var(--border)', fontSize: '11px', marginBottom: '2px' }}>
                    <code style={{ color: 'var(--green)', wordBreak: 'break-all' }}>{line}</code>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}


// ─── Ads.txt / app-ads.txt per-node result ───────────────────────────────────
function AdsTxtNodeResult({ adsTxtCheck, node }) {
  if (!adsTxtCheck) return null;
  const nr = adsTxtCheck.nodeResults?.find(r => r.asi === node.asi && String(r.sid) === String(node.sid));
  if (!nr) return null;
  return (
    <div style={{ marginTop:'10px' }}>
      <div style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>
        Publisher ads.txt / app-ads.txt
        <span className="badge badge-muted" style={{ marginLeft:'6px', fontSize:'9px' }}>{adsTxtCheck.publisherDomain}</span>
      </div>
      <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
        {['ads.txt','app-ads.txt'].map(fileType => {
          const f = nr.files?.[fileType];
          if (!f) return null;
          if (!f.fetched) return (
            <span key={fileType} className="badge badge-muted">
              {fileType}: not found (HTTP {f.httpStatus || 'error'})
            </span>
          );
          if (!f.found) return (
            <span key={fileType} className="badge badge-red">
              <XCircle size={10}/> {fileType}: not listed
            </span>
          );
          return (
            <div key={fileType} style={{ marginBottom:'4px' }}>
              <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginBottom:'3px' }}>
                <span className="badge badge-green"><CheckCircle size={10}/> {fileType}: found</span>
                {f.isDirect   && <span className="badge badge-cyan">DIRECT</span>}
                {f.isReseller && <span className="badge badge-purple">RESELLER</span>}
                {f.count > 1  && <span className="badge badge-amber">{f.count}× duplicate</span>}
              </div>
              {f.lines?.map((line, li) => (
                <div key={li} style={{ background:'var(--bg-base)', padding:'3px 8px', borderRadius:'3px', border:'1px solid var(--border)', fontSize:'11px', marginBottom:'2px' }}>
                  <code style={{ color:'var(--green)', wordBreak:'break-all' }}>{line}</code>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── IssueList helper ─────────────────────────────────────────────────────────
function IssueList({ items, variant }) {
  if (!items?.length) return null;
  const cls = variant === 'error' ? 'alert-error' : 'alert-warning';
  const Icon = variant === 'error' ? XCircle : AlertTriangle;
  return (
    <div style={{ marginBottom:'6px' }}>
      {items.map((msg, i) => (
        <div key={i} className={`alert ${cls}`} style={{ marginBottom:'3px', padding:'4px 8px', fontSize:'11px' }}>
          <Icon size={10}/> {msg}
        </div>
      ))}
    </div>
  );
}

// ─── Rich schain result section ───────────────────────────────────────────────
function SchainResultSection({ result }) {
  if (!result) return null;

  // Normalise API response into the shape used by BidRequestAnalyzer's SchainSection
  const s = {
    valid:               result.valid,
    errors:              result.errors || [],
    warnings:            result.warnings || [],
    ver:                 result.summary?.version,
    complete:            result.summary?.complete,
    nodeCount:           result.summary?.nodeCount,
    nodeResults:         result.nodeResults || [],
    authorizationStatus: result.authorizationStatus || null,
    crossNodeFailed:     result.crossNodeFailed || false,
    hasOrderIssues:      result.hasOrderIssues || false,
    orderIssues:         result.orderIssues || [],
    adsTxtCheck:         result.adsTxtCheck || null,
    pubDomainInfo:       result.pubDomainInfo || null,
    structurallyValid:   result.structurallyValid !== false,
  };

  const fullyOk = s.authorizationStatus === 'authorized' && !s.crossNodeFailed && !s.hasOrderIssues;
  const partial = s.authorizationStatus === 'not_authorized' || s.crossNodeFailed || s.hasOrderIssues;

  return (
    <div className="card">
      {/* Header badges */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap', alignItems:'center' }}>
        <div className="card-title" style={{ margin:0, flex:1 }}>
          <Link2 size={14}/> Schain Validation Results
        </div>
        <span className={`badge ${s.structurallyValid ? 'badge-green' : 'badge-red'}`}>
          {s.structurallyValid ? '✓ Structurally Valid' : '✗ Structurally Invalid'}
        </span>
        {fullyOk
          ? <span className="badge badge-green"><CheckCircle size={10}/> Fully Authorized</span>
          : partial
          ? <span className="badge badge-amber"><ShieldAlert size={10}/> Partially Authorized</span>
          : <span className="badge badge-muted">Auth Unknown</span>}
      </div>

      {/* Top-level chain info */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
        <span className="badge badge-muted">ver: {s.ver || '?'}</span>
        <span className={`badge ${s.complete === 1 ? 'badge-green' : 'badge-amber'}`}>
          complete: {s.complete} {s.complete === 1 ? '(full chain)' : '(partial chain)'}
        </span>
        <span className="badge badge-cyan">{s.nodeCount} node{s.nodeCount !== 1 ? 's' : ''}</span>
      </div>

      {/* Composite verdict banner */}
      {fullyOk ? (
        <div className="alert alert-success" style={{ marginBottom:'12px' }}>
          <CheckCircle size={13}/>
          <div><strong>✅ Fully Authorized & Transparent</strong> — All nodes are listed in sellers.json, app-ads.txt, cross-node chain integrity validated, and node order is correct.</div>
        </div>
      ) : partial ? (
        <div className="alert alert-warning" style={{ marginBottom:'12px' }}>
          <ShieldAlert size={13}/>
          <div>
            <strong>⚠️ Partially Authorized</strong> — One or more checks failed:
            <ul style={{ margin:'6px 0 0 16px', fontSize:'11px', lineHeight:1.8 }}>
              {s.authorizationStatus === 'not_authorized' && <li>One or more nodes not found in publisher's app-ads.txt / ads.txt</li>}
              {s.nodeResults?.some(nr => nr.directViolation) && (
                <li>Non-first schain node(s) listed as DIRECT — must be RESELLER</li>
              )}
              {s.nodeResults?.some(nr => nr.directIntermediaryConflict) && (
                <li>DIRECT in ads.txt conflicts with INTERMEDIARY in sellers.json</li>
              )}
              {s.crossNodeFailed && <li>Cross-node chain integrity failed — sid domain mismatch between consecutive nodes</li>}
              {s.hasOrderIssues && <li>Node order issue — publisher/intermediary sequence violates IAB spec</li>}
            </ul>
          </div>
        </div>
      ) : !s.adsTxtCheck ? (
        // Only show "unknown" warning when no domain info was available at all
        <div className="alert alert-warning" style={{ marginBottom:'12px' }}>
          <AlertTriangle size={13}/>
          Authorization status unknown — no publisher store URL or domain provided, so ads.txt / app-ads.txt check was skipped.
        </div>
      ) : null}

      {/* complete=0 advice */}
      {s.complete === 0 && (
        <div className="alert alert-warning" style={{ marginBottom:'12px' }}>
          <AlertTriangle size={12}/>
          <div><strong>complete=0:</strong> Partial supply chain — not all intermediaries are listed. Set complete=1 only when the full chain is represented.</div>
        </div>
      )}

      {/* Publisher domain info */}
      {s.adsTxtCheck ? (
        <div className="alert alert-info" style={{ marginBottom:'12px' }}>
          <Info size={12}/>
          <div style={{ flex:1 }}>
            <div>Developer domain: <strong>{s.adsTxtCheck.publisherDomain}</strong>
              <span className="badge badge-muted" style={{ marginLeft:'6px', fontSize:'10px' }}>{s.adsTxtCheck.domainSource}</span>
            </div>
            <div style={{ display:'flex', gap:'8px', marginTop:'4px', fontSize:'11px' }}>
              <span className={`badge ${s.adsTxtCheck.adsTxtFetched ? 'badge-green' : 'badge-muted'}`}>
                ads.txt: {s.adsTxtCheck.adsTxtFetched ? '✓' : '✗'} HTTP {s.adsTxtCheck.adsTxtStatus || 'error'}
              </span>
              <span className={`badge ${s.adsTxtCheck.appAdsTxtFetched ? 'badge-green' : 'badge-muted'}`}>
                app-ads.txt: {s.adsTxtCheck.appAdsTxtFetched ? '✓' : '✗'} HTTP {s.adsTxtCheck.appAdsTxtStatus || (s.adsTxtCheck.appAdsTxtFetched ? '' : 'undefined')}
              </span>
            </div>
          </div>
        </div>
      ) : s.pubDomainInfo?.error ? (
        <div className="alert alert-warning" style={{ marginBottom:'12px' }}>
          <AlertTriangle size={12}/>
          <div><strong>Could not fetch developer domain:</strong> {s.pubDomainInfo.error}</div>
        </div>
      ) : null}

      {/* Node order issues */}
      {s.hasOrderIssues && s.orderIssues?.length > 0 && (
        <div style={{ padding:'10px 12px', background:'color-mix(in srgb, var(--amber) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--amber) 25%, transparent)', borderRadius:'var(--radius-sm)', marginBottom:'12px' }}>
          <div style={{ fontWeight:700, fontSize:'12px', color:'var(--amber)', marginBottom:'6px' }}>⚠️ Node Order Issues</div>
          <div style={{ fontSize:'11px', color:'var(--text-secondary)', marginBottom:'6px', lineHeight:1.6 }}>
            IAB spec: Node 1 = Publisher/direct seller (PUBLISHER type, hp=1). Downstream nodes = Intermediaries/Resellers in chain order.
          </div>
          {s.orderIssues.map((issue, idx) => (
            <div key={idx} style={{ fontSize:'11px', color:'var(--amber)', marginTop:'4px' }}>! {issue}</div>
          ))}
        </div>
      )}

      {/* Top-level errors/warnings (non-node) */}
      <IssueList items={s.errors.filter(e => !e.includes('Node') && !s.orderIssues?.includes(e))} variant="error" />
      <IssueList items={s.warnings.filter(w => !w.includes('Node') && !s.orderIssues?.includes(w))} variant="warning" />

      {/* Node-by-node */}
      {s.nodeResults.length > 0 && (
        <div style={{ marginTop:'12px' }}>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'8px' }}>
            Chain Nodes — Sellers.json + Publisher Ads.txt Validation
          </div>
          {s.nodeResults.map((nr, i) => {
            const sj = nr.sellersJson;
            const hasErr  = !nr.valid || sj?.found === false || nr.authorized === false;
            const hasWarn = nr.orderIssue || (nr.crossNodeCheck?.checked && !nr.crossNodeCheck.domainMatch);
            return (
              <div key={i} style={{
                padding:'12px', marginBottom:'8px',
                background: hasErr ? 'color-mix(in srgb, var(--red) 4%, transparent)' : hasWarn ? 'color-mix(in srgb, var(--amber) 5%, transparent)' : 'color-mix(in srgb, var(--green) 4%, transparent)',
                border:`1px solid ${hasErr ? 'color-mix(in srgb, var(--red) 20%, transparent)' : hasWarn ? 'color-mix(in srgb, var(--amber) 25%, transparent)' : 'color-mix(in srgb, var(--green) 15%, transparent)'}`,
                borderRadius:'var(--radius-sm)',
              }}>
                {/* Node header */}
                <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'8px', flexWrap:'wrap' }}>
                  <span style={{ background:'var(--cyan-dim)', color:'var(--cyan)', borderRadius:'3px', padding:'1px 7px', fontSize:'11px', fontWeight:700 }}>
                    Node {nr.index}
                  </span>
                  <span style={{ fontWeight:600, color:'var(--text-primary)', fontSize:'13px' }}>{nr.node?.asi || '?'}</span>
                  <span className="badge badge-muted">sid: {nr.node?.sid}</span>
                  {nr.node?.name && <span className="badge badge-muted">{nr.node.name}</span>}
                  <span className={`badge ${nr.node?.hp === 1 ? 'badge-green' : 'badge-amber'}`}>hp={nr.node?.hp}</span>
                  {/* Auth badge */}
                  {(() => {
                    const crossFail = nr.crossNodeCheck?.checked && !nr.crossNodeCheck.domainMatch;
                    const fullyAuth = nr.authorized === true && !crossFail && !nr.orderIssue;
                    const unauth    = nr.authorized === false || crossFail;
                    return fullyAuth
                      ? <span className="badge badge-green" style={{ marginLeft:'auto' }}><CheckCircle size={10}/> Authorized {nr.authSource ? `(${nr.authSource})` : ''}</span>
                      : unauth
                      ? <span className="badge badge-red" style={{ marginLeft:'auto' }}><ShieldAlert size={10}/> Unauthorized</span>
                      : <span className="badge badge-muted" style={{ marginLeft:'auto' }}>Auth Unknown</span>;
                  })()}
                  {/* Order issue badges */}
                  {nr.orderIssue === 'first_node_is_intermediary' && <span className="badge badge-amber"><AlertTriangle size={10}/> Expected Publisher — got Intermediary</span>}
                  {nr.orderIssue === 'mid_node_is_publisher'      && <span className="badge badge-amber"><AlertTriangle size={10}/> Mid-chain Publisher</span>}
                  {nr.orderIssue === 'hp_zero_intermediate'       && <span className="badge badge-amber"><AlertTriangle size={10}/> hp=0 on intermediate</span>}
                </div>

                {/* Optional fields */}
                {(nr.node?.rid || nr.node?.domain) && (
                  <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'8px', fontFamily:'var(--font-mono)', display:'flex', gap:'12px' }}>
                    {nr.node.rid    && <span>rid: {nr.node.rid}</span>}
                    {nr.node.domain && <span>domain: {nr.node.domain}</span>}
                  </div>
                )}

                {/* Node-level errors/warnings */}
                <IssueList items={nr.errors}   variant="error" />
                <IssueList items={nr.warnings} variant="warning" />

                {/* Sellers.json — always expanded, full entry shown */}
                <div style={{ marginTop:'8px', display:'flex', gap:'8px', alignItems:'flex-start', flexWrap:'wrap' }}>
                  <span style={{ fontSize:'10px', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginTop:'3px', flexShrink:0 }}>
                    sellers.json:
                  </span>
                  {!sj ? (
                    <span className="badge badge-muted">No domain to look up</span>
                  ) : !sj.fetched ? (
                    <span className="badge badge-amber"><AlertTriangle size={10}/> {sj.error}</span>
                  ) : !sj.found ? (
                    <span className="badge badge-red">
                      <XCircle size={10}/> sid "{nr.node?.sid}" not found in {nr.node?.asi} sellers.json ({sj.totalSellers} sellers checked)
                    </span>
                  ) : (
                    <div>
                      <span className="badge badge-green"><CheckCircle size={10}/> Found in sellers.json</span>
                      <div style={{ marginTop:'6px', padding:'8px 10px', background:'var(--bg-base)', borderRadius:'3px', border:'1px solid var(--border)', fontSize:'11px', fontFamily:'var(--font-mono)', lineHeight:1.8 }}>
                        {[['seller_id', sj.seller?.seller_id], ['name', sj.seller?.name], ['domain', sj.seller?.domain],
                          ['seller_type', sj.seller?.seller_type],
                          ['is_confidential', sj.seller?.is_confidential !== undefined ? String(sj.seller.is_confidential) : undefined]]
                          .filter(([,v]) => v !== undefined && v !== null && v !== '')
                          .map(([k,v]) => (
                            <div key={k} style={{ display:'flex', gap:'8px' }}>
                              <span style={{ color:'var(--text-muted)', minWidth:'120px' }}>{k}:</span>
                              <span style={{ color:'var(--cyan)' }}>{String(v)}</span>
                            </div>
                          ))}
                        <div style={{ color:'var(--text-muted)', fontSize:'10px', marginTop:'2px' }}>
                          {sj.totalSellers} total sellers in {nr.node?.asi} sellers.json
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Ads.txt / app-ads.txt per-node result */}
                <AdsTxtNodeResult adsTxtCheck={s.adsTxtCheck} node={nr.node} />

                {/* ── Check 2: Non-first node listed as DIRECT ── */}
                {nr.directViolation && (
                  <div style={{ marginTop:'10px', padding:'10px 12px', borderRadius:'var(--radius-sm)',
                    background:'color-mix(in srgb, var(--red) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--red) 35%, transparent)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                      <ShieldAlert size={12} style={{ color:'var(--red)', flexShrink:0 }} />
                      <span style={{ fontWeight:700, fontSize:'11px', color:'var(--red)' }}>
                        DIRECT Violation — Non-First Node
                      </span>
                      <span className="badge badge-red" style={{ fontSize:'9px' }}>Unauthorized</span>
                    </div>
                    <div style={{ fontSize:'11px', color:'var(--text-secondary)', lineHeight:1.6 }}>
                      {nr.directViolation.reason}
                    </div>
                    <div style={{ marginTop:'6px', fontSize:'11px', color:'var(--amber)' }}>
                      <strong>Fix:</strong> Change{' '}
                      <code style={{ background:'color-mix(in srgb, var(--red) 10%, transparent)', padding:'1px 5px', borderRadius:'3px', color:'var(--red)' }}>
                        {nr.directViolation.asi}, {nr.directViolation.sid}, DIRECT
                      </code>{' '}to{' '}
                      <code style={{ background:'color-mix(in srgb, var(--green) 8%, transparent)', padding:'1px 5px', borderRadius:'3px', color:'var(--green)' }}>
                        {nr.directViolation.asi}, {nr.directViolation.sid}, RESELLER
                      </code>{' '}in the publisher's {nr.directViolation.fileType}.
                    </div>
                  </div>
                )}

                {/* ── Check 3: DIRECT in ads.txt but INTERMEDIARY in sellers.json ── */}
                {nr.directIntermediaryConflict && (
                  <div style={{ marginTop:'10px', padding:'10px 12px', borderRadius:'var(--radius-sm)',
                    background:'color-mix(in srgb, var(--red) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--red) 35%, transparent)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
                      <ShieldAlert size={12} style={{ color:'var(--red)', flexShrink:0 }} />
                      <span style={{ fontWeight:700, fontSize:'11px', color:'var(--red)' }}>
                        DIRECT / INTERMEDIARY Conflict
                      </span>
                      <span className="badge badge-red" style={{ fontSize:'9px' }}>Unauthorized</span>
                    </div>
                    <div style={{ display:'flex', gap:'8px', marginBottom:'6px', flexWrap:'wrap' }}>
                      <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>Publisher {nr.directIntermediaryConflict.fileType}:</span>
                      <code style={{ fontSize:'10px', background:'color-mix(in srgb, var(--red) 8%, transparent)', padding:'1px 6px', borderRadius:'3px', color:'var(--red)' }}>DIRECT</code>
                      <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>{nr.directIntermediaryConflict.asi}/sellers.json:</span>
                      <code style={{ fontSize:'10px', background:'color-mix(in srgb, var(--amber) 8%, transparent)', padding:'1px 6px', borderRadius:'3px', color:'var(--amber)' }}>INTERMEDIARY</code>
                    </div>
                    <div style={{ fontSize:'11px', color:'var(--text-secondary)', lineHeight:1.6 }}>
                      {nr.directIntermediaryConflict.reason}
                    </div>
                    <div style={{ marginTop:'6px', fontSize:'11px', color:'var(--amber)' }}>
                      <strong>Fix:</strong> Change{' '}
                      <code style={{ background:'color-mix(in srgb, var(--red) 10%, transparent)', padding:'1px 5px', borderRadius:'3px', color:'var(--red)' }}>
                        {nr.directIntermediaryConflict.asi}, {nr.directIntermediaryConflict.sid}, DIRECT
                      </code>{' '}to{' '}
                      <code style={{ background:'color-mix(in srgb, var(--green) 8%, transparent)', padding:'1px 5px', borderRadius:'3px', color:'var(--green)' }}>
                        {nr.directIntermediaryConflict.asi}, {nr.directIntermediaryConflict.sid}, RESELLER
                      </code>{' '}in the publisher's {nr.directIntermediaryConflict.fileType}.
                    </div>
                  </div>
                )}

                {/* Cross-node chain validation — only shown when FAILED */}
                {nr.crossNodeCheck?.checked && !nr.crossNodeCheck.domainMatch && (
                  <div style={{ marginTop:'10px', padding:'10px 12px', borderRadius:'var(--radius-sm)', background:'color-mix(in srgb, var(--red) 6%, transparent)', border:'1px solid color-mix(in srgb, var(--red) 35%, transparent)' }}>
                    <div style={{ fontSize:'11px', fontWeight:800, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px', color:'var(--red)' }}>
                      ❌ Cross-node: Mismatch
                    </div>
                    <div style={{ fontSize:'11px', color:'var(--text-secondary)', lineHeight:1.7, fontFamily:'var(--font-mono)' }}>
                      <div>
                        <span style={{ color:'var(--text-muted)' }}>Upstream (Node {nr.index - 1}): </span>
                        <span style={{ color:'var(--cyan)' }}>{nr.crossNodeCheck.prevNodeAsi}</span>
                      </div>
                      <div>
                        <span style={{ color:'var(--text-muted)' }}>This node ({nr.crossNodeCheck.thisNode}) sid: </span>
                        <span style={{ color:'var(--cyan)' }}>{nr.crossNodeCheck.thisSid}</span>
                        <span style={{ color:'var(--text-muted)' }}> → domain in sellers.json: </span>
                        <span style={{ color:'var(--red)', fontWeight:700 }}>{nr.crossNodeCheck.resolvedDomain || 'not found'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SchainValidator() {
  const [input, setInput] = useState('');
  const [publisherDomain, setPublisherDomain] = useState('');
  const [storeurl, setStoreurl] = useState('');
  const [bundleId, setBundleId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState('validate');

  const [jsonErr, setJsonErr] = useState(''); // inline JSON format error

  // ── Quick Entry mode ─────────────────────────────────────────────────────────
  const [inputMode, setInputMode] = useState('json'); // 'json' | 'quick'
  const [quickAsi, setQuickAsi] = useState('');
  const [quickSid, setQuickSid] = useState('');
  const [quickErr, setQuickErr] = useState('');

  function validateJson(raw) {
    setInput(raw);
    if (!raw.trim()) { setJsonErr(''); return; }
    try { JSON.parse(raw); setJsonErr(''); }
    catch (e) { setJsonErr(e.message); }
  }

  function fixJsonErrors() {
    if (!input.trim()) return;
    try {
      let fixed = input;

      // 1. Remove trailing commas before } or ]
      fixed = fixed.replace(/,(\s*[}\]])/g, '$1');

      // 2. Wrap unquoted keys:  key: → "key":
      fixed = fixed.replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)(\s*:)/g, '$1"$2"$3');

      // 3. Replace single-quoted strings with double-quoted
      fixed = fixed.replace(/'([^'\\]*(\\.[^'\\]*)*)'/g, '"$1"');

      // 4. Remove JS-style // and /* */ comments
      fixed = fixed.replace(/\/\/[^\n]*/g, '');
      fixed = fixed.replace(/\/\*[\s\S]*?\*\//g, '');

      // 5. Try to extract just the schain object if wrapped in a larger object
      //    e.g. { source: { schain: {...} } } → extract the schain value
      let parsed;
      try {
        parsed = JSON.parse(fixed);
      } catch {
        // Try extracting the innermost {...} block that contains "nodes"
        const match = fixed.match(/(\{[^{}]*"nodes"\s*:\s*\[[\s\S]*?\][^{}]*\})/);
        if (match) {
          try { parsed = JSON.parse(match[1]); fixed = match[1]; } catch {}
        }
      }

      if (parsed) {
        // If it's a nested object, try to find the schain inside
        if (!parsed.nodes && !parsed.ver) {
          const findSchain = (obj) => {
            if (!obj || typeof obj !== 'object') return null;
            if (obj.nodes && obj.ver !== undefined) return obj;
            for (const v of Object.values(obj)) {
              const found = findSchain(v);
              if (found) return found;
            }
            return null;
          };
          const schain = findSchain(parsed);
          if (schain) parsed = schain;
        }
        const pretty = JSON.stringify(parsed, null, 2);
        setInput(pretty);
        setJsonErr('');
        toast.success('JSON fixed — ready to validate');
      } else {
        toast.error('Could not auto-fix — JSON is too malformed. Please fix manually.');
      }
    } catch {
      toast.error('Could not auto-fix — please correct the JSON manually.');
    }
  }

  // Builder state
  const [complete, setComplete] = useState(1);
  const [nodes, setNodes] = useState([{ asi: '', sid: '', hp: 1, rid: '', name: '', domain: '' }]);
  const [buildResult, setBuildResult] = useState(null);

  // Resolution status for all three paths
  const [resolveStatus, setResolveStatus] = useState(null); // null | 'loading' | 'ok' | 'error'
  const [resolveLabel, setResolveLabel] = useState('');

  async function handleValidate() {
    if (!input.trim()) return toast.error('Paste a schain object');
    setLoading(true); setResult(null); setResolveStatus(null); setResolveLabel('');
    try {
      let resolvedStoreUrl  = undefined;
      let resolvedDomain    = undefined;

      const bid  = bundleId.trim();
      const surl = storeurl.trim();
      const dom  = publisherDomain.trim();

      // ── Priority 1: Bundle ID → store URL → backend resolves domain + app-ads.txt
      if (bid) {
        setResolveStatus('loading'); setResolveLabel('Resolving bundle ID…');
        try {
          const { data } = await axios.post('/api/bundle-lookup/lookup', { bundleIds: [bid] });
          const r = data?.results?.[0];
          if (r?.storeUrl) {
            resolvedStoreUrl = r.storeUrl;
            setResolveStatus('ok'); setResolveLabel(`Store URL resolved from bundle`);
          } else {
            setResolveStatus('error'); setResolveLabel('Bundle not found on store — validation will proceed without domain check');
            toast(`Bundle "${bid}" not found — proceeding without ads.txt check`, { icon: '⚠️', duration: 5000 });
          }
        } catch { setResolveStatus('error'); setResolveLabel('Bundle lookup failed'); }

      // ── Priority 2: Store URL → backend resolves domain + app-ads.txt
      } else if (surl) {
        resolvedStoreUrl = surl;
        setResolveStatus('ok'); setResolveLabel('Using provided store URL');

      // ── Priority 3: Domain → backend uses directly for app-ads.txt
      } else if (dom) {
        resolvedDomain = dom;
        setResolveStatus('ok'); setResolveLabel('Using provided domain for ads.txt check');
      }

      const { data } = await axios.post('/api/schain/validate', {
        schain:          input.trim(),
        storeurl:        resolvedStoreUrl,
        publisherDomain: resolvedDomain,
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Validation error');
    } finally { setLoading(false); }
  }

  // ── Quick Entry validation ───────────────────────────────────────────────────
  async function handleQuickValidate() {
    const asiList = quickAsi.split('|').map(s => s.trim()).filter(Boolean);
    const sidList = quickSid.split('|').map(s => s.trim()).filter(Boolean);

    if (asiList.length === 0) return toast.error('Enter at least one ASI value');
    if (sidList.length === 0) return toast.error('Enter at least one SID value');
    if (asiList.length !== sidList.length) {
      setQuickErr(`ASI count (${asiList.length}) does not match SID count (${sidList.length}) — each ASI must have a corresponding SID`);
      return;
    }
    setQuickErr('');

    // Build a valid schain object from the pipe-separated values
    const schain = {
      ver: '1.0',
      complete: 1,
      nodes: asiList.map((asi, i) => ({ asi, sid: sidList[i], hp: 1 }))
    };
    const schainJson = JSON.stringify(schain, null, 2);

    setLoading(true); setResult(null); setResolveStatus(null); setResolveLabel('');
    try {
      let resolvedStoreUrl = undefined;
      let resolvedDomain   = undefined;

      const bid  = bundleId.trim();
      const surl = storeurl.trim();
      const dom  = publisherDomain.trim();

      if (bid) {
        setResolveStatus('loading'); setResolveLabel('Resolving bundle ID…');
        try {
          const { data } = await axios.post('/api/bundle-lookup/lookup', { bundleIds: [bid] });
          const r = data?.results?.[0];
          if (r?.storeUrl) {
            resolvedStoreUrl = r.storeUrl;
            setResolveStatus('ok'); setResolveLabel('Store URL resolved from bundle');
          } else {
            setResolveStatus('error'); setResolveLabel('Bundle not found — proceeding without ads.txt check');
            toast(`Bundle "${bid}" not found — proceeding without ads.txt check`, { icon: '⚠️', duration: 5000 });
          }
        } catch { setResolveStatus('error'); setResolveLabel('Bundle lookup failed'); }
      } else if (surl) {
        resolvedStoreUrl = surl;
        setResolveStatus('ok'); setResolveLabel('Using provided store URL');
      } else if (dom) {
        resolvedDomain = dom;
        setResolveStatus('ok'); setResolveLabel('Using provided domain for ads.txt check');
      }

      const { data } = await axios.post('/api/schain/validate', {
        schain:          schainJson,
        storeurl:        resolvedStoreUrl,
        publisherDomain: resolvedDomain,
      });

      // Also populate JSON textarea so user can inspect the constructed schain
      setInput(schainJson);
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Validation error');
    } finally { setLoading(false); }
  }

  async function handleBuild() {
    const cleanNodes = nodes.map(n => {
      const node = { asi: n.asi, sid: n.sid, hp: parseInt(n.hp) };
      if (n.rid) node.rid = n.rid;
      if (n.name) node.name = n.name;
      if (n.domain) node.domain = n.domain;
      return node;
    });
    setLoading(true); setBuildResult(null);
    try {
      const { data } = await axios.post('/api/schain/build', {
        nodes: cleanNodes,
        complete: parseInt(complete),
        publisherDomain: publisherDomain.trim() || undefined,
        storeurl: storeurl.trim() || undefined
      });
      setBuildResult(data);
      toast.success('schain object built');
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  }

  function addNode() { setNodes([...nodes, { asi: '', sid: '', hp: 1, rid: '', name: '', domain: '' }]); }
  function removeNode(i) { setNodes(nodes.filter((_, idx) => idx !== i)); }
  function updateNode(i, field, value) { const n = [...nodes]; n[i][field] = value; setNodes(n); }

  return (
    <div>
      <h1 className="page-title">Supply Chain (schain) Validator</h1>
      <p className="page-subtitle">Validate OpenRTB SupplyChain objects per IAB Tech Lab specification v1.0</p>

      <div className="tabs">
        <button className={`tab ${tab === 'validate' ? 'active' : ''}`} onClick={() => setTab('validate')}>Validate Schain</button>
        <button className={`tab ${tab === 'build' ? 'active' : ''}`} onClick={() => setTab('build')}>Build schain</button>
      </div>

      {tab === 'validate' && (
        <div className="card" style={{ marginBottom: '16px' }}>

          {/* ── Mode toggle ─────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', background: 'var(--bg-base)', padding: '3px', borderRadius: 'var(--radius-sm)', width: 'fit-content', border: '1px solid var(--border)' }}>
            <button
              className={`btn btn-sm ${inputMode === 'json' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ fontSize: '11px', padding: '4px 12px' }}
              onClick={() => { setInputMode('json'); setResult(null); setQuickErr(''); }}>
              { } JSON Mode
            </button>
            <button
              className={`btn btn-sm ${inputMode === 'quick' ? 'btn-primary' : 'btn-secondary'}`}
              style={{ fontSize: '11px', padding: '4px 12px' }}
              onClick={() => { setInputMode('quick'); setResult(null); setJsonErr(''); }}>
              ⚡ Quick Entry
            </button>
          </div>

          {/* ── JSON Mode ───────────────────────────────────────────────────── */}
          {inputMode === 'json' && (<>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
              <div className="card-title"><Link2 size={14} /> schain JSON</div>
              <button className="btn btn-secondary btn-sm" onClick={() => setInput(JSON.stringify(EXAMPLE_SCHAIN, null, 2))}>
                Load Example
              </button>
            </div>
            <textarea
              className="input-field w-full"
              style={{
                minHeight: '200px', fontFamily: 'var(--font-mono)', fontSize: '12px',
                border: jsonErr ? '1.5px solid var(--red)' : undefined,
                outline: 'none',
              }}
              placeholder={'{\n  "ver": "1.0",\n  "complete": 1,\n  "nodes": [...]\n}'}
              value={input}
              onChange={e => validateJson(e.target.value)}
            />
            {jsonErr && (
              <div style={{ marginTop:'4px', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
                <div style={{ fontSize:'11px', color:'var(--red)', display:'flex', alignItems:'center', gap:'5px' }}>
                  <XCircle size={11}/> Invalid JSON — {jsonErr}
                </div>
                <button className="btn btn-secondary btn-sm"
                  style={{ fontSize:'11px', borderColor:'color-mix(in srgb, var(--amber) 40%, transparent)', color:'var(--amber)' }}
                  onClick={fixJsonErrors}>
                  ✦ Fix JSON Errors
                </button>
              </div>
            )}
          </>)}

          {/* ── Quick Entry Mode ─────────────────────────────────────────────── */}
          {inputMode === 'quick' && (<>
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px', lineHeight: 1.6 }}>
                Enter pipe-separated (<code style={{ color: 'var(--cyan)' }}>|</code>) ASI and SID values — one per node in chain order.
                The tool will construct and validate a complete schain object automatically.
              </div>

              {/* ASI input */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Schain ASI <span style={{ color: 'var(--cyan)', fontWeight: 400, textTransform: 'none' }}>(advertising system domains, pipe-separated)</span>
                </label>
                <input
                  className="input-field w-full"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}
                  placeholder="inmobi.com|loopme.com|adwmg.com"
                  value={quickAsi}
                  onChange={e => { setQuickAsi(e.target.value); setQuickErr(''); }}
                />
              </div>

              {/* SID input */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Schain SID <span style={{ color: 'var(--cyan)', fontWeight: 400, textTransform: 'none' }}>(seller IDs, pipe-separated — must match ASI count)</span>
                </label>
                <input
                  className="input-field w-full"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: '12px' }}
                  placeholder="44ea200bb4944eccb8456fe7c7e3a0b5|9724|100765"
                  value={quickSid}
                  onChange={e => { setQuickSid(e.target.value); setQuickErr(''); }}
                />
              </div>

              {/* Live node preview */}
              {quickAsi && quickSid && (() => {
                const asiList = quickAsi.split('|').map(s => s.trim()).filter(Boolean);
                const sidList = quickSid.split('|').map(s => s.trim()).filter(Boolean);
                const countMatch = asiList.length === sidList.length;
                return (
                  <div style={{ marginBottom: '8px', padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: `1px solid ${countMatch ? 'color-mix(in srgb, var(--cyan) 20%, transparent)' : 'color-mix(in srgb, var(--red) 20%, transparent)'}` }}>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                      Node Preview — {asiList.length} ASI · {sidList.length} SID
                      {countMatch
                        ? <span style={{ color: 'var(--green)', marginLeft: '8px' }}>✓ counts match</span>
                        : <span style={{ color: 'var(--red)', marginLeft: '8px' }}>✗ count mismatch</span>}
                    </div>
                    {asiList.map((asi, i) => (
                      <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '3px', fontSize: '11px', fontFamily: 'var(--font-mono)' }}>
                        <span style={{ color: 'var(--text-muted)', minWidth: '50px' }}>Node {i + 1}</span>
                        <span style={{ color: 'var(--cyan)' }}>{asi}</span>
                        <span style={{ color: 'var(--text-muted)' }}>·</span>
                        <span style={{ color: sidList[i] ? 'var(--text-secondary)' : 'var(--red)' }}>
                          {sidList[i] || '⚠ missing SID'}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {quickErr && (
                <div className="alert alert-error" style={{ marginBottom: '8px', fontSize: '11px' }}>
                  <XCircle size={11}/> {quickErr}
                </div>
              )}
            </div>
          </>)}

          {/* ── Publisher identity options (shared by both modes) ────────────── */}
          <div style={{ marginTop: inputMode === 'quick' ? '0' : '8px', display: 'flex', flexDirection: 'column', gap: '0' }}>

            {/* ── Option 1: Bundle ID */}
            <div style={{ padding:'10px 12px', background:'color-mix(in srgb, var(--cyan) 3%, transparent)', border:'1px solid color-mix(in srgb, var(--cyan) 15%, transparent)', borderRadius:'var(--radius-sm) var(--radius-sm) 0 0', borderBottom:'none' }}>
              <div style={{ fontSize:'10px', color:'var(--cyan)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>
                {inputMode === 'quick' ? 'Bundle ID' : 'Option 1 — Bundle ID'}
                <span style={{ color:'var(--text-muted)', fontWeight:400, textTransform:'none', letterSpacing:0 }}> (recommended · auto-fetches store URL → developer domain → app-ads.txt)</span>
              </div>
              <input
                className="input-field w-full"
                placeholder="e.g. com.example.app or 1234567890"
                value={bundleId}
                onChange={e => { setBundleId(e.target.value); setResolveStatus(null); }}
              />
            </div>

            {/* OR divider */}
            <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'4px 12px', background:'var(--bg-elevated)', borderLeft:'1px solid color-mix(in srgb, var(--cyan) 15%, transparent)', borderRight:'1px solid color-mix(in srgb, var(--cyan) 15%, transparent)' }}>
              <div style={{ flex:1, height:'1px', background:'var(--border)' }}/>
              <span style={{ fontSize:'10px', color:'var(--text-muted)', fontWeight:700 }}>OR</span>
              <div style={{ flex:1, height:'1px', background:'var(--border)' }}/>
            </div>

            {/* ── Option 2: Store URL */}
            <div style={{ padding:'10px 12px', background:'color-mix(in srgb, var(--cyan) 3%, transparent)', borderLeft:'1px solid color-mix(in srgb, var(--cyan) 15%, transparent)', borderRight:'1px solid color-mix(in srgb, var(--cyan) 15%, transparent)', borderBottom:'none' }}>
              <div style={{ fontSize:'10px', color:'var(--cyan)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>
                {inputMode === 'quick' ? 'Store URL' : 'Option 2 — App Store / Play Store URL'}
                <span style={{ color:'var(--text-muted)', fontWeight:400, textTransform:'none', letterSpacing:0 }}> (fetches developer domain → app-ads.txt)</span>
              </div>
              <input
                className="input-field w-full"
                placeholder="e.g. https://apps.apple.com/app/id123456 or https://play.google.com/store/apps/details?id=com.example"
                value={storeurl}
                onChange={e => { setStoreurl(e.target.value); setResolveStatus(null); }}
              />
            </div>

            {/* OR divider */}
            <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'4px 12px', background:'var(--bg-elevated)', borderLeft:'1px solid color-mix(in srgb, var(--cyan) 15%, transparent)', borderRight:'1px solid color-mix(in srgb, var(--cyan) 15%, transparent)' }}>
              <div style={{ flex:1, height:'1px', background:'var(--border)' }}/>
              <span style={{ fontSize:'10px', color:'var(--text-muted)', fontWeight:700 }}>OR</span>
              <div style={{ flex:1, height:'1px', background:'var(--border)' }}/>
            </div>

            {/* ── Option 3: Publisher domain */}
            <div style={{ padding:'10px 12px', background:'color-mix(in srgb, var(--cyan) 3%, transparent)', border:'1px solid color-mix(in srgb, var(--cyan) 15%, transparent)', borderRadius:'0 0 var(--radius-sm) var(--radius-sm)', borderTop:'none' }}>
              <div style={{ fontSize:'10px', color:'var(--cyan)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>
                {inputMode === 'quick' ? 'Publisher Domain' : 'Option 3 — Publisher Domain'}
                <span style={{ color:'var(--text-muted)', fontWeight:400, textTransform:'none', letterSpacing:0 }}> (used directly for app-ads.txt check)</span>
              </div>
              <input
                className="input-field w-full"
                placeholder="e.g. example.com"
                value={publisherDomain}
                onChange={e => { setPublisherDomain(e.target.value); setResolveStatus(null); }}
              />
            </div>

            {/* Resolution status */}
            {resolveStatus && (
              <div style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'8px', fontSize:'11px',
                color: resolveStatus === 'ok' ? 'var(--green)' : resolveStatus === 'error' ? 'var(--amber)' : 'var(--text-muted)' }}>
                {resolveStatus === 'loading' && <span className="spinner" style={{ width:11, height:11, borderWidth:2 }}/>}
                {resolveStatus === 'ok'      && <CheckCircle size={11}/>}
                {resolveStatus === 'error'   && <AlertTriangle size={11}/>}
                {resolveLabel}
              </div>
            )}

            {/* Validate button */}
            <div style={{ marginTop:'10px' }}>
              <button
                className="btn btn-primary"
                onClick={inputMode === 'quick' ? handleQuickValidate : handleValidate}
                disabled={loading || (inputMode === 'json' && !!jsonErr)}
                style={{ width:'100%' }}>
                {loading
                  ? <><span className="spinner"/> {bundleId.trim() ? 'Resolving & Validating…' : 'Validating…'}</>
                  : <><CheckCircle size={14}/> Validate Schain</>}
              </button>
            </div>

          </div>
        </div>
      )}

      {tab === 'validate' && result && <SchainResultSection result={result} />}

      {tab === 'build' && (
        <div>
          <div className="card" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
              <div className="card-title"><Link2 size={14} /> Build schain Object</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px' }}>
                <label style={{ color: 'var(--text-muted)' }}>complete:</label>
                <select className="select-field" value={complete} onChange={e => setComplete(e.target.value)} style={{ padding: '4px 8px' }}>
                  <option value={1}>1 (full chain)</option>
                  <option value={0}>0 (partial)</option>
                </select>
              </div>
            </div>

            {nodes.map((node, i) => (
              <div key={i} style={{ padding: '12px', marginBottom: '8px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'var(--cyan)', fontWeight: 600 }}>Node {i + 1}</span>
                  {nodes.length > 1 && <button className="btn btn-danger btn-sm" onClick={() => removeNode(i)}><Trash2 size={11} /></button>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: '6px', marginBottom: '6px' }}>
                  <input className="input-field" placeholder="asi (required)" value={node.asi} onChange={e => updateNode(i, 'asi', e.target.value)} />
                  <input className="input-field" placeholder="sid (required)" value={node.sid} onChange={e => updateNode(i, 'sid', e.target.value)} />
                  <select className="select-field" value={node.hp} onChange={e => updateNode(i, 'hp', e.target.value)}>
                    <option value={1}>hp=1</option>
                    <option value={0}>hp=0</option>
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
                  <input className="input-field" placeholder="rid (optional)" value={node.rid} onChange={e => updateNode(i, 'rid', e.target.value)} />
                  <input className="input-field" placeholder="name (optional)" value={node.name} onChange={e => updateNode(i, 'name', e.target.value)} />
                  <input className="input-field" placeholder="domain (optional)" value={node.domain} onChange={e => updateNode(i, 'domain', e.target.value)} />
                </div>
              </div>
            ))}

            <div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <input
                className="input-field w-full"
                placeholder="App Store / Play Store URL (recommended)"
                value={storeurl}
                onChange={e => setStoreurl(e.target.value)}
              />
              <input
                className="input-field w-full"
                placeholder="Or enter publisher domain manually (e.g. example.com)"
                value={publisherDomain}
                onChange={e => setPublisherDomain(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary" onClick={addNode}><Plus size={13} /> Add Node</button>
              <button className="btn btn-primary" onClick={handleBuild} disabled={loading}>
                {loading ? <span className="spinner" /> : <Link2 size={13} />}
                Build & Validate
              </button>
            </div>
          </div>

          {buildResult && (
            <div className="card">
              <div className={`alert ${buildResult.validation.valid ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '12px' }}>
                {buildResult.validation.valid ? <CheckCircle size={14} /> : <XCircle size={14} />}
                {buildResult.validation.valid ? 'Valid schain object' : `${buildResult.validation.errors.length} validation errors`}
              </div>
              <div className="card-title" style={{ marginBottom: '8px' }}>Generated schain JSON</div>
              <pre className="code-display">{buildResult.schainString}</pre>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: '8px' }}
                onClick={() => { navigator.clipboard.writeText(buildResult.schainString); toast.success('Copied!'); }}>
                Copy JSON
              </button>
              {buildResult.validation?.adsTxtCheck && <AdsTxtSummary atc={buildResult.validation.adsTxtCheck} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
